import type { MilestoneStep } from '@/types';

import {
  type CreateMilestoneStepParams,
  type DeleteMilestoneStepParams,
  type GetMilestoneStepParams,
  type GetMilestoneStepsParams,
  type RefreshMilestoneSuggestedStepParams,
  type UpdateMilestoneStepParams,
  useCreateMilestoneStepMutation,
  useDeleteMilestoneStepMutation,
  useGetMilestoneStepQuery,
  useGetMilestoneStepsQuery,
  useRefreshMilestoneSuggestedStepMutation,
  useUpdateMilestoneStepMutation,
} from '@/store/api/milestoneStepApi';


export const useGetMilestoneSteps = (
  params: GetMilestoneStepsParams,
  options?: { enabled?: boolean },
) =>
  useGetMilestoneStepsQuery(params, {
    skip: options?.enabled === false,
  });

export const useGetMilestoneStep = (params: GetMilestoneStepParams) =>
  useGetMilestoneStepQuery(params);

// Mutations with React Query compatibility layer
export const useCreateMilestoneStep = () => {
  const [trigger, result] = useCreateMilestoneStepMutation();
  return {
    mutate: (variables: CreateMilestoneStepParams, options?: {
      onSuccess?: (data: MilestoneStep) => void;
      onError?: (error: unknown) => void;
      onSettled?: () => void;
    }) => {
      trigger(variables)
        .unwrap()
        .then((data) => {
          options?.onSuccess?.(data);
          options?.onSettled?.();
        })
        .catch((error) => {
          options?.onError?.(error);
          options?.onSettled?.();
        });
    },
    ...result,
  };
};

export const useUpdateMilestoneStep = () => {
  const [trigger, result] = useUpdateMilestoneStepMutation();
  return {
    mutate: (variables: UpdateMilestoneStepParams, options?: {
      onSuccess?: (data: MilestoneStep) => void;
      onError?: (error: unknown) => void;
      onSettled?: () => void;
    }) => {
      trigger(variables)
        .unwrap()
        .then((data) => {
          options?.onSuccess?.(data);
          options?.onSettled?.();
        })
        .catch((error) => {
          options?.onError?.(error);
          options?.onSettled?.();
        });
    },
    ...result,
  };
};

export const useDeleteMilestoneStep = () => {
  const [trigger, result] = useDeleteMilestoneStepMutation();
  return {
    mutate: (variables: DeleteMilestoneStepParams, options?: {
      onSuccess?: (data: void) => void;
      onError?: (error: unknown) => void;
      onSettled?: () => void;
    }) => {
      trigger(variables)
        .unwrap()
        .then((data) => {
          options?.onSuccess?.(data);
          options?.onSettled?.();
        })
        .catch((error) => {
          options?.onError?.(error);
          options?.onSettled?.();
        });
    },
    ...result,
  };
};

export const useRefreshSuggestedMilestoneStep = () => {
  const [trigger, result] = useRefreshMilestoneSuggestedStepMutation();
  return {
    mutate: (variables: RefreshMilestoneSuggestedStepParams, options?: {
      onSuccess?: (data: Array<string>) => void;
      onError?: (error: unknown) => void;
      onSettled?: () => void;
    }) => {
      trigger(variables)
        .unwrap()
        .then((data) => {
          options?.onSuccess?.(data);
          options?.onSettled?.();
        })
        .catch((error) => {
          options?.onError?.(error);
          options?.onSettled?.();
        });
    },
    ...result,
  };
};
